import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiClock, FiFileText, FiCheckCircle, FiXCircle, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';

const TestSeriesDetail = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { test } = location.state || {};
  const { course } = location.state || {};

  const [examDetails, setExamDetails] = useState(null);
  const [attemptData, setAttemptData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExamDetails();
  }, [examId]);

  const fetchExamDetails = async () => {
    try {
      const response = await quizAPI.getExamDetails(examId, user.id);
      if (response.data.success) {
        setExamDetails(response.data.exam);
        setAttemptData(response.data.attempt_data);
      }
    } catch (error) {
      console.error('Error fetching exam details:', error);
    } finally {
      setLoading(false);
    }
  };

  const startExam = async () => {
    try {
      setLoading(true);
      // Fetch exam questions
      const response = await quizAPI.getExamQuestions(examId);

      if (response.data.success && response.data.questions.length > 0) {
        // Navigate to quiz page with questions
        navigate('/quiz', {
          state: {
            questions: response.data.questions,
            config: {
              numQuestions: response.data.questions.length,
              difficulty: 'medium',
              useAI: false,
              examId: examId,
              examTitle: test?.title || examDetails?.exam,
              marks: test?.marks || examDetails?.marks || 1,
              negativeMarks: test?.negative_marks || examDetails?.negative_marks || 0,
              isExam: true,
              examDetails: examDetails
            }
          }
        });
      } else {
        alert('No questions found for this exam. Please contact support.');
      }
    } catch (error) {
      console.error('Error fetching exam questions:', error);
      alert('Failed to load exam questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Test Series Details</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-10xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading exam details...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Exam Title Card */}
            <div className="card bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">
                    {test?.title || examDetails?.exam}
                  </h2>
                  {examDetails?.description && (
                    <p className="text-white/90">{examDetails.description}</p>
                  )}
                </div>
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <FiFileText className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Exam Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card text-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <FiFileText className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Total Questions</p>
                <p className="text-2xl font-bold text-gray-800">
                  {examDetails?.total_questions || test?.total_questions || '?'}
                </p>
              </div>

              <div className="card text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <FiCheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Marks</p>
                <p className="text-2xl font-bold text-green-600">
                  +{examDetails?.total_questions
                    ? ((test?.marks || examDetails?.marks || 0) / examDetails.total_questions).toFixed(2)
                    : (test?.marks || examDetails?.marks || 1)
                  }
                </p>
              </div>

              <div className="card text-center">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                  <FiXCircle className="w-6 h-6 text-red-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Negative</p>
                <p className="text-2xl font-bold text-red-600">
                  -{test?.negative_marks || examDetails?.negative_marks || 0}
                </p>
              </div>

              <div className="card text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                  <FiRefreshCw className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Attempts Left</p>
                <p className="text-2xl font-bold text-purple-600">
                  {test?.attempt || examDetails?.attempt || '∞'}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="card">
              <div className="flex items-center mb-4">
                <FiAlertCircle className="w-6 h-6 text-yellow-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-800">Instructions</h3>
              </div>

              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">1.</span>
                  <span>Read each question carefully before selecting your answer.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">2.</span>
                  <span>You can navigate between questions using Next/Previous buttons.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">3.</span>
                  <span>Your answers are automatically saved as you progress.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">4.</span>

                  <span>
                    Marks per question  is {
                      examDetails?.total_questions
                        ? ((test?.marks || examDetails?.marks || 0) / examDetails.total_questions).toFixed(2)
                        : (test?.marks || examDetails?.marks || 1)
                    }.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">5.</span>
                  <span>Each wrong answer deducts {test?.negative_marks || 0} marks.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">6.</span>
                  <span>You can review all questions before final submission.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3 font-bold">7.</span>
                  <span>Once submitted, you cannot change your answers.</span>
                </li>
              </ul>
            </div>

 {/* Start Test Button */}
 <div className="card bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">Ready to start?</h3>
                  <p className="text-sm text-gray-600">
                    Make sure you have a stable internet connection
                  </p>
                </div>
                {test?.is_free ? (
                  <button
                    onClick={startExam}
                    className="btn-primary px-8 py-4 text-lg shadow-lg hover:shadow-xl whitespace-nowrap w-full md:w-auto"
                  >
                    Start Test Now
                  </button>
                ) : course?.purchased > 0 ? (
                  <button
                    onClick={startExam}
                    className="btn-primary px-8 py-4 text-lg shadow-lg hover:shadow-xl whitespace-nowrap w-full md:w-auto"
                  >
                    Start Test Now
                  </button>
                ) : (
                  <Link
                    to={`/course-details/${course?.id || ''}`}
                    className="btn-primary px-8 py-4 text-lg shadow-lg hover:shadow-xl whitespace-nowrap w-full md:w-auto"
                  >
                    Buy Course
                  </Link>
                )}



              </div>
            </div>
            
            {/* Previous Attempts */}
            {attemptData && attemptData.length > 0 && (
              <div className="card">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Previous Attempts</h3>
                <div className="space-y-3">
                  {attemptData.map((attempt, index) => {
                    // Calculate actual score (total_marks - negative_marks)
                    const actualScore = (attempt.total_marks || 0) - (attempt.negative_marks || 0);

                    // Get total marks and questions from exam details
                    const totalQuestions = examDetails?.questions || test?.questions || 0;
                    const marksPerQuestion = examDetails?.marks || test?.marks || 1;
                    const maxScore = totalQuestions * marksPerQuestion;

                    // Calculate percentage
                    const percentage = maxScore > 0 ? ((actualScore / maxScore) * 100).toFixed(2) : 0;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">
                            Attempt {index + 1}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(attempt.cdate).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${percentage >= 70 ? 'text-green-600' :
                              percentage >= 50 ? 'text-yellow-600' :
                                'text-red-600'
                            }`}>
                            {actualScore}/{maxScore}
                          </p>
                          <p className="text-sm text-gray-600">{percentage}%</p>
                          <p className="text-xs text-gray-500 mt-1">
                            (+{attempt.total_marks} | -{attempt.negative_marks})
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

           
          </div>
        )}
      </main>
    </div>
  );
};

export default TestSeriesDetail;

